// Who still has required configuration to fill in, for every surface that says so:
// the "!" badge on a node, the one on an arrow, and the Quick Start banner's steps.
// They all read this one function so they cannot contradict each other — a step
// warning above a node showing a check is exactly the bug this consolidates away.

import {
  actionNeedsSetup,
  isAuth,
  isDormantAction,
  isFlowAction,
} from 'components/common/configPanel/actionSetup';
import {
  EARTH_RANGER_TYPE,
  exemptActionSlugs,
  isExemptAction,
  payloadParsingNotRequired,
} from 'components/common/configPanel/tempExemptions';
import {
  PROVIDER_TYPES_WITH_BASE_URL,
  baseUrlIsRequired,
} from 'components/routes/providerBaseUrl';
import { AccountBundle } from './flowMapTypes';
import { FlowArrow } from './useFlowGraph';

export type NeedsSetupReport = {
  // Ids of the accounts whose node carries a "!". Arrows never carry one —
  // every action is configured on its own node, so a flow gap badges the node
  // that owes the configuration.
  needsSetupAccounts: Set<string>;
  // What the banner counts, and what its "look for the exclamation marks"
  // sentence promises the user will find on the map.
  needsSetupCount: number;
  providerNeedsSetup: boolean;
  destinationsNeedSetup: boolean;
};

// Which flow directions an account is NOT currently on the hook for. An account
// owes its pull configuration only while an enabled arrow leaves it, and its push
// configuration only while an enabled arrow enters it. Everything else is dormant:
// editable as an FYI, but never badged — demanding that config up front reports a
// finished route as incomplete. Shared with the node panel's nav so the badge on
// the node and the badge on the section can't disagree.
export function dormantFlowTypes(
  accountId: string,
  arrows: FlowArrow[],
): Set<string> {
  const live = new Set<string>();
  // An account no arrow touches at all (a Route with no destination yet) has no
  // map context to be dormant IN — nothing is dormant, so its own sections keep
  // their badges. Otherwise a fresh Route's primary pull config would read as
  // "not required" and its Quick Start step as completed.
  let touched = false;
  arrows.forEach((arrow) => {
    if (arrow.fromId !== accountId && arrow.toId !== accountId) return;
    touched = true;
    if (arrow.state !== 'enabled') return;
    if (arrow.fromId === accountId) live.add('pull');
    if (arrow.toId === accountId) live.add('push');
  });
  const dormant = new Set<string>();
  if (!touched) return dormant;
  if (!live.has('pull')) dormant.add('pull');
  if (!live.has('push')) dormant.add('push');
  return dormant;
}

// GUNDI-5680 temporary hack (see tempExemptions.ts for the reviewer note): which
// of this account's actions never badge, given
// who is at the other end(s) of the Route. THE one place that decides it — the
// node badge (computeNeedsSetup) and the section badges (the panel's nav) both
// call this, so they cannot disagree about the account's technology or peers.
// A destination whose bundle hasn't loaded still has a type on the Route record.
export function exemptSlugsFor(
  bundle: AccountBundle,
  ctx: {
    providerId: string;
    routeProvider: any;
    bundles: Record<string, AccountBundle>;
    destinations: any[];
  },
): Set<string> {
  const { providerId, routeProvider, bundles, destinations } = ctx;
  const techOf = (id: string, fallback?: string) =>
    bundles[id]?.techValue || fallback || '';
  const ownTech = techOf(
    bundle.id,
    bundle.isProvider ? routeProvider?.type?.value : undefined,
  );
  // Cheap exit before building the peer list: only EarthRanger has exemptions.
  if (ownTech !== EARTH_RANGER_TYPE) return exemptActionSlugs(ownTech, []);
  const peers: string[] = [];
  if (bundle.id !== providerId)
    peers.push(techOf(providerId, routeProvider?.type?.value));
  destinations.forEach((d: any) => {
    if (d.id !== bundle.id) peers.push(techOf(d.id, d?.type?.value));
  });
  return exemptActionSlugs(ownTech, peers.filter(Boolean));
}

export function computeNeedsSetup(input: {
  providerId: string;
  routeProvider: any;
  bundles: Record<string, AccountBundle>;
  destinations: any[];
  arrows: FlowArrow[];
}): NeedsSetupReport {
  const { providerId, routeProvider, bundles, destinations, arrows } = input;

  // An account/arrow "needs setup" while any of its actions with required fields
  // has no saved configuration. Optional-only actions don't count — nothing
  // blocks the flow without them.
  const needsConfig = (
    bundle: AccountBundle | undefined,
    pred: (a: any) => boolean,
  ) =>
    !!bundle &&
    bundle.actions.some((a: any) => pred(a) && actionNeedsSetup(bundle, a));

  const needsSetupAccounts = new Set<string>();
  Object.values(bundles).forEach((b) => {
    // Of the non-flow actions only credentials count — whatever `type` they
    // carry, since credentials are never dormant (also the nav's rule, so
    // the section badge and the node badge can't disagree about them). The
    // Advanced Config helpers (on-demand lookups and the like) have
    // required schema fields but nothing in the flow waits on them.
    // Pull/push are configured on the node too, but only count once their
    // direction is actually carrying data — that is decided per direction
    // below.
    if (needsConfig(b, isAuth)) needsSetupAccounts.add(b.id);
  });
  // Webhook providers also need their payload parsing configured — except
  // InReach, where it is never required (GUNDI-5680 temporary hack).
  if (
    routeProvider?.type?.webhook &&
    !routeProvider?.webhook_configuration?.data &&
    !payloadParsingNotRequired(routeProvider?.type?.value)
  ) {
    needsSetupAccounts.add(providerId);
  }
  // So does a provider whose technology is reached at a URL the user supplies.
  if (
    PROVIDER_TYPES_WITH_BASE_URL.includes(routeProvider?.type?.value) &&
    !(bundles[providerId]?.base_url || routeProvider?.base_url)
  ) {
    needsSetupAccounts.add(providerId);
  }
  // And so does a destination with no address, now that the node's own panel asks
  // for one. The technologies that derive their address from configuration are
  // exempt, as they are in the panel.
  Object.values(bundles).forEach((b) => {
    if (!b.isProvider && !b.base_url && baseUrlIsRequired(b.techValue)) {
      needsSetupAccounts.add(b.id);
    }
  });

  // A flow gap has an owner: the account the data comes from owes the pull
  // configuration, the one it goes to owes the push. Every action is configured
  // on its own node now, so the gap badges that node — arrows carry no badge.
  const flowGapAccounts = new Set<string>();
  Object.values(bundles).forEach((b) => {
    // Only the directions that are actually carrying data. A direction the
    // user hasn't switched on has nothing missing — its panel already says to
    // configure the sections and then turn it on, so demanding that config up
    // front reports a finished route as incomplete.
    const dormant = dormantFlowTypes(b.id, arrows);
    // GUNDI-5680 temporary hack: EarthRanger's pull sections don't count in a
    // Route with InReach. Same helper the node panel's nav calls.
    const exempt = exemptSlugsFor(b, {
      providerId,
      routeProvider,
      bundles,
      destinations,
    });
    if (
      needsConfig(
        b,
        (a: any) =>
          isFlowAction(a) &&
          !isAuth(a) &&
          !isDormantAction(a, dormant) &&
          !isExemptAction(a, exempt),
      )
    ) {
      flowGapAccounts.add(b.id);
    }
  });
  flowGapAccounts.forEach((id) => needsSetupAccounts.add(id));

  return {
    needsSetupAccounts,
    needsSetupCount: needsSetupAccounts.size,
    // "Is this side fully configured?" — a wider question than the node
    // badge's "does this node's own panel have work in it".
    // An account whose integration failed to load has no bundle, and "couldn't
    // be inspected" must not be reported as "configured" — the unknown side
    // counts as needing setup, which is what the load-failure banner above the
    // map is already telling the user to retry.
    providerNeedsSetup:
      !bundles[providerId] ||
      needsSetupAccounts.has(providerId) ||
      flowGapAccounts.has(providerId),
    // Same question asked of the other side, for the same reason.
    destinationsNeedSetup: destinations.some(
      (d: any) =>
        !bundles[d.id] ||
        needsSetupAccounts.has(d.id) ||
        flowGapAccounts.has(d.id),
    ),
  };
}
